/**
 * navigation/rootNavigator.tsx — top-level gate for SK Queue.
 *
 * Two axes decide what renders:
 *  1. PUBLIC vs ADMIN surface (from the surface router):
 *     • PUBLIC (Guest QR, Now Serving display) → render standalone, NO auth, no
 *       shell. A guest's phone / a door TV must never hit a login wall.
 *     • ADMIN (Host, Operator, Live View, Voice, Chat, Config) → require a
 *       platform session.
 *  2. For admin surfaces, the SESSION probe (same model as every SK product):
 *       boot → GET /v1/me (via Backbone)
 *         200 → render the admin shell
 *         401 → bounce to the centralized SK Platform auth (?product=queue),
 *               which returns here signed-in via the shared cookie.
 *
 * SK Queue owns no login UI — the platform does (see platformAuthRedirect).
 */

import React, { useEffect, useRef } from 'react';
import { View, ActivityIndicator, Platform } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useSession } from '../context/sessionContext';
import { useSurfaceRouter } from './surfaceRouter';
import { ActiveSurface } from './surfaceScreens';
import AppShell from './appShell';
import QueueToast from '../queue/components/queueToast';
import { redirectToNotInPlan, redirectToPlatformAuth } from './platformAuthRedirect';
import { isNotInPlan } from '../lib/planGate';

export default function RootNavigator() {
  const { theme } = useTheme();
  const { colors } = theme;
  const { isPublic } = useSurfaceRouter();
  const { refresh, loading, isAuthenticated, subscribedProducts } = useSession();
  const probed = useRef(false);

  // Probe the session once on boot (only matters for admin surfaces).
  useEffect(() => {
    if (probed.current) return;
    probed.current = true;
    refresh();
  }, [refresh]);

  // Public surfaces never gate — render immediately regardless of session.
  if (isPublic) {
    return (
      <View style={{ flex: 1 }}>
        <ActiveSurface />
        <QueueToast />
      </View>
    );
  }

  // Admin surface: wait for the probe, then shell or redirect.
  if (loading) {
    return (
      <View style={{
        flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper,
        ...(Platform.OS === 'web' ? { height: '100vh' as any } : {}),
      }}>
        <ActivityIndicator size="large" color={colors.ink} />
      </View>
    );
  }

  const notInPlan = Platform.OS === 'web' && isNotInPlan(subscribedProducts);
  if (!isAuthenticated || notInPlan) {
    // No session → hand off to the platform's login. Signed in, but this org's
    // plan does not include the Queue website (SK Salon / SK Fleet run Queue
    // underneath without selling it) → the platform's not-in-plan page, session
    // kept; no list = open; web only. Render nothing while the browser
    // navigates away.
    if (isAuthenticated) redirectToNotInPlan();
    else redirectToPlatformAuth();
    return (
      <View style={{ flex: 1, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper }}>
        <ActivityIndicator size="large" color={colors.ink} />
      </View>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      <AppShell />
      <QueueToast />
    </View>
  );
}
